<template>
  <div class="custom-table-card bg-white overflow-hidden mb-4">
    <!-- Header: Title and Icon -->
    <div class="table-header-section pt-4 px-4 pb-2">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <div class="d-flex align-items-center gap-2">
          <i v-if="titleIcon" :class="titleIcon + ' text-dark'" style="font-size: 1.25rem;"></i>
          <h4 class="fw-bold text-dark m-0">{{ title }}</h4>
        </div>
      </div>
      
      <!-- Subtitle Description -->
      <p class="text-muted mb-2 small">{{ subtitle }}</p>

      <!-- Emulated Tab Design with Dark Underline and Full-Width Bottom border -->
      <div v-if="tabs && tabs.length > 0" class="tabs-container mb-4">
        <ul class="nav nav-tabs border-bottom-0 gap-4" role="tablist">
          <li v-for="tab in tabs" :key="getTabKey(tab)" class="nav-item" role="presentation">
            <button 
              class="nav-link border-0 bg-transparent px-0 py-2 position-relative" 
              :class="{ 'active-tab-style text-dark fw-bold': activeTab === getTabKey(tab), 'text-muted': activeTab !== getTabKey(tab) }"
              type="button" 
              @click="switchTab(getTabKey(tab))"
            >
              {{ getTabLabel(tab) }}
              <span v-if="activeTab === getTabKey(tab)" class="active-tab-line"></span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Aligned Input Controls & Spacing -->
      <div class="d-flex align-items-center justify-content-between gap-3 mb-3 mt-3">
        <!-- Left Side: Total count and Search Input (UX Priority 1) -->
        <div class="d-flex align-items-center gap-3 flex-grow-1" style="max-width: 50%;">
          <span v-if="filteredData.length > 0" class="fw-semibold text-dark text-nowrap" style="font-size: 0.95rem;">
            {{ filteredData.length }} {{ title }}
          </span>
          <div v-if="enableSearch" class="input-group search-input-group border">
            <span class="input-group-text border-0 bg-transparent">
              <i class="fa fa-search text-muted small"></i>
            </span>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control border-0 bg-transparent py-1"
              placeholder="Search"
            />
          </div>
        </div>

        <!-- Right Side: Date Picker, Dropdowns, Export & Customize Columns (UX Priority 2) -->
        <div class="d-flex align-items-center gap-2">
          <!-- Inline Date Range Filter -->
          <div v-if="enableDateFilter" class="d-flex align-items-center gap-2 me-2">
            <input
              v-model="startDate"
              type="date"
              class="form-control date-range-picker"
            />
            <span class="small text-muted">-</span>
            <input
              v-model="endDate"
              type="date"
              class="form-control date-range-picker"
            />
          </div>

          <!-- Dropdown Filters -->
          <div v-for="filter in dropdownFilters" :key="filter.key" class="dropdown">
            <button 
              class="btn btn-outline-secondary dropdown-toggle d-flex align-items-center justify-content-between gap-2 filter-btn rounded-2"
              type="button" 
              :id="`dropdown-${filter.key}`"
              data-bs-toggle="dropdown" 
              aria-expanded="false"
            >
              <span class="fw-normal">{{ getFilterDisplayText(filter) }}</span>
              <span v-if="selectedFilters[filter.key]" class="badge bg-light text-dark p-1 ms-1">
                {{ Array.isArray(selectedFilters[filter.key]) ? selectedFilters[filter.key].length : 1 }}
              </span>
            </button>
            <ul class="dropdown-menu p-2 shadow border-0" :aria-labelledby="`dropdown-${filter.key}`" style="min-width: 200px; border-radius: 8px;">
              <li v-if="selectedFilters[filter.key]">
                <button class="dropdown-item text-danger d-flex align-items-center" @click="clearFilter(filter.key)">
                  <i class="fa fa-times me-2"></i>Clear Filter
                </button>
                <hr class="dropdown-divider">
              </li>
              
              <!-- Multiple selection -->
              <li v-if="filter.multiple" v-for="option in filter.options" :key="option.value">
                <label class="dropdown-item-text d-flex align-items-center cursor-pointer hover-bg-light p-2 rounded">
                  <input
                    type="checkbox"
                    class="form-check-input me-2 mt-0"
                    :value="option.value"
                    :checked="isFilterSelected(filter.key, option.value)"
                    @change="toggleMultipleFilter(filter.key, option.value)"
                  />
                  <span class="flex-grow-1 small text-dark">{{ option.label }}</span>
                  <span v-if="option.count !== undefined" class="badge bg-light text-muted small ms-2">{{ option.count }}</span>
                </label>
              </li>

              <!-- Single selection -->
              <li v-if="!filter.multiple" v-for="option in filter.options" :key="option.value">
                <button
                  class="dropdown-item d-flex align-items-center justify-content-between rounded-2"
                  :class="{ 'active': selectedFilters[filter.key] === option.value }"
                  @click="setSingleFilter(filter.key, option.value)"
                >
                  <span>{{ option.label }}</span>
                  <span v-if="option.count !== undefined" class="badge bg-light text-muted">{{ option.count }}</span>
                </button>
              </li>

              <li v-if="filter.options.length === 0">
                <span class="dropdown-item-text text-muted small">No options available</span>
              </li>
            </ul>
          </div>

          <!-- Customize Columns Dropdown triggered by bullet list icon button -->
          <div v-if="showSettings" class="dropdown">
            <button 
              class="btn btn-outline-secondary d-flex align-items-center justify-content-center filter-icon-btn" 
              type="button" 
              id="table-settings-dropdown" 
              data-bs-toggle="dropdown" 
              aria-expanded="false" 
              title="Customize columns"
            >
              <i class="fas fa-list-ul"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end p-3 shadow border-0 customize-columns-dropdown-menu" aria-labelledby="table-settings-dropdown" style="min-width: 260px; border-radius: 8px;" @click.stop>
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="text-dark fw-bold m-0 small text-uppercase tracking-wider">Customize columns</h6>
                <button class="btn btn-link btn-sm text-primary p-0 border-0 text-decoration-none small" @click="resetColumnVisibility">Reset</button>
              </div>
              <hr class="my-2">
              <li 
                v-for="(column, idx) in localColumns" 
                :key="column.key" 
                class="py-1 d-flex align-items-center gap-2 dropdown-item-custom"
                draggable="true"
                @dragstart="onDragStart($event, idx)"
                @dragover.prevent
                @drop="onDrop($event, idx)"
              >
                <i class="fas fa-grip-vertical text-muted opacity-50 cursor-grab" style="font-size: 0.8rem;"></i>
                <div class="form-check m-0 d-flex align-items-center gap-2">
                  <input class="form-check-input mt-0 shadow-none" type="checkbox" :id="'col-vis-' + column.key" v-model="visibleColumns[column.key]">
                  <label class="form-check-label small text-dark user-select-none" :for="'col-vis-' + column.key">{{ column.label }}</label>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Active Filters Display -->
      <div v-if="hasActiveFilters" class="mt-2 d-flex flex-wrap gap-2 pb-2">
        <span class="badge filter-pill" v-if="searchQuery">
          Search: "{{ searchQuery }}"
          <i class="fa fa-times ms-1 cursor-pointer" @click="searchQuery = ''"></i>
        </span>
        <span class="badge filter-pill" v-if="startDate">
          From: {{ formatFilterDate(startDate) }}
          <i class="fa fa-times ms-1 cursor-pointer" @click="startDate = ''"></i>
        </span>
        <span class="badge filter-pill" v-if="endDate">
          To: {{ formatFilterDate(endDate) }}
          <i class="fa fa-times ms-1 cursor-pointer" @click="endDate = ''"></i>
        </span>
        <template v-for="(value, key) in selectedFilters" :key="key">
          <span v-if="value" class="badge filter-pill">
            {{ getFilterLabel(key) }}: 
            <template v-if="Array.isArray(value)">{{ value.length }} selected</template>
            <template v-else>{{ getFilterOptionLabel(key, value) }}</template>
            <i class="fa fa-times ms-1 cursor-pointer" @click="clearFilter(key)"></i>
          </span>
        </template>
        <button class="btn btn-sm btn-outline-danger py-0 px-2 rounded-pill small" @click="clearAllFilters">
          <i class="fa fa-times me-1"></i>Clear All
        </button>
      </div>
    </div>

    <!-- Table Container -->
    <div class="table-responsive border-top border-bottom px-0">
      <table class="table table-hover align-middle mb-0 custom-styled-table">
        <thead>
          <tr class="bg-light-header">
            <!-- Global Select Checkbox -->
            <th v-if="showCheckboxes" class="px-4 py-3 align-middle" style="width: 48px;">
              <input type="checkbox" class="form-check-input" :checked="isAllSelected" @change="toggleSelectAll" />
            </th>
            
            <th
              v-for="column in localColumns"
              v-show="isColumnVisible(column.key)"
              :key="column.key"
              class="text-dark small fw-semibold px-4 py-3 cursor-pointer user-select-none"
              @click="sortBy(column.key)"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ column.label }}</span>
                <div class="ms-2 d-inline-block">
                  <i
                    v-if="sortColumn === column.key"
                    :class="{
                      'fas fa-chevron-up': sortDirection === 'asc',
                      'fas fa-chevron-down': sortDirection === 'desc'
                    }"
                    class="text-primary small"
                  ></i>
                  <i v-else class="fas fa-sort text-muted opacity-25 small"></i>
                </div>
              </div>
            </th>
            <th v-if="hasActions" class="text-end text-muted small fw-semibold px-4 py-3" style="width: 80px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in paginatedData" :key="getRowKey(item, index)" class="table-row-hover">
            <!-- Row Checkbox -->
            <td v-if="showCheckboxes" class="px-4 py-3 align-middle">
              <input type="checkbox" class="form-check-input" :checked="isRowSelected(item)" @change="toggleRowSelection(item)" />
            </td>
            
            <!-- Dynamic Slots or Default Cells -->
            <slot name="row" :item="item" :index="index" :visible="isColumnVisible">
              <td v-for="column in localColumns" v-show="isColumnVisible(column.key)" :key="column.key" class="px-4 py-3">
                <span v-if="column.type === 'date'" class="text-muted small">
                  {{ formatDate(getNestedValue(item, column.key)) }}
                </span>
                <span v-else-if="column.type === 'currency'" class="small text-dark font-monospace">
                  ₦{{ formatCurrency(getNestedValue(item, column.key)) }}
                </span>
                <span v-else-if="column.type === 'badge'" 
                      :class="`badge ${getBadgeClass(getNestedValue(item, column.key))} rounded-pill px-3 py-2`">
                  {{ getNestedValue(item, column.key) }}
                </span>
                <span v-else class="small text-muted">
                  {{ getNestedValue(item, column.key) }}
                </span>
              </td>
            </slot>
            
            <!-- Row Actions Far Right -->
            <td v-if="hasActions" class="px-4 py-3 align-middle text-end">
              <slot name="actions" :item="item" :index="index">
                <div class="dropdown">
                  <button 
                    class="btn btn-link text-muted p-1 border-0 rounded-circle hover-bg-light" 
                    type="button" 
                    data-bs-toggle="dropdown"
                    style="width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;"
                  >
                    <i class="fas fa-ellipsis-h"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end shadow border-0" style="border-radius: 8px;">
                    <li><a class="dropdown-item" href="#" @click.prevent="$emit('view', item)"><i class="fa fa-eye me-2 small"></i>View</a></li>
                    <li><a class="dropdown-item" href="#" @click.prevent="$emit('edit', item)"><i class="fas fa-pencil-alt me-2 small"></i>Edit</a></li>
                    <li><hr class="dropdown-divider"></li>
                    <li><a class="dropdown-item text-danger" href="#" @click.prevent="$emit('delete', item)"><i class="fa fa-trash me-2 small"></i>Delete</a></li>
                  </ul>
                </div>
              </slot>
            </td>
          </tr>
          
          <!-- Empty Data State -->
          <tr v-if="!currentData || currentData.length === 0 || filteredData.length === 0">
            <td :colspan="localColumns.length + (hasActions ? 1 : 0) + (showCheckboxes ? 1 : 0)" class="py-5 text-muted border-0">
              <div class="d-flex flex-column align-items-center justify-content-center text-center w-100">
                <slot name="empty">
                  <i class="fa fa-inbox fs-2 mb-3 opacity-25"></i>
                  <div>
                    <p class="mb-1 fw-medium text-dark">No data found</p>
                    <small v-if="searchQuery || startDate || endDate" class="text-muted small">Try adjusting your search criteria or filters</small>
                  </div>
                </slot>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Emulated Footer Pagination from screenshot -->
    <div v-if="filteredData.length > 0" class="d-flex align-items-center justify-content-between p-4">
      <!-- Left: Total results and pages count -->
      <div class="text-muted small">
        {{ filteredData.length }} Results | Page {{ currentPage }} of {{ totalPages }}
      </div>
      
      <!-- Center: Square box controls [<<] [<] [Page Box] [>] [>>] -->
      <div v-if="totalPages > 1" class="d-flex align-items-center gap-1">
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(1)"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <span class="border rounded px-3 py-1 bg-white fw-bold small text-dark mx-2" style="border-color: #d1d5db; min-width: 32px; text-align: center;">
          {{ currentPage }}
        </span>
        
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="currentPage === totalPages"
          @click="goToPage(totalPages)"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>
      </div>

      <!-- Right: Switch items per page dynamically -->
      <div class="d-flex align-items-center gap-2">
        <span class="small text-muted text-nowrap">Items per page:</span>
        <button 
          v-for="size in [10, 20, 50, 100]" 
          :key="size" 
          class="btn btn-link btn-sm p-0 text-decoration-none items-per-page-toggle"
          :class="itemsPerPage === size ? 'fw-bold text-dark text-underline' : 'text-muted'"
          @click="setItemsPerPage(size)"
        >
          {{ size }}
        </button>
      </div>
    </div>

    <!-- Bottom Alert Block -->
    <div v-if="alertMessage" class="px-4 pb-4">
      <div class="alert-banner d-flex align-items-center gap-3 p-3">
        <div class="alert-icon-container">
          <i class="fas fa-lightbulb"></i>
        </div>
        <span class="alert-text small fw-medium">{{ alertMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, toNative, Prop, Watch } from 'vue-facing-decorator'

interface TableColumn {
  key: string
  label: string
  type?: 'text' | 'date' | 'currency' | 'badge'
  sortable?: boolean
  searchable?: boolean
}

interface DropdownFilter {
  key: string
  label: string
  options: FilterOption[]
  multiple?: boolean
  placeholder?: string
}

interface FilterOption {
  value: string | number
  label: string
  count?: number
}

@Component({
  emits: ['view', 'edit', 'delete', 'sort', 'filter', 'page-change', 'tab-change']
})
class TableComponent extends Vue {
  @Prop({ type: Array, required: true })
  readonly data!: any[]

  @Prop({ type: Array, required: true })
  readonly columns!: TableColumn[]

  @Prop({ type: String, default: 'Data Table' })
  readonly title!: string

  @Prop({ type: String, default: '' })
  readonly titleIcon!: string

  @Prop({ type: String, default: 'Overview of data' })
  readonly subtitle!: string

  @Prop({ type: Boolean, default: true })
  readonly enableSearch!: boolean

  @Prop({ type: Boolean, default: false })
  readonly enableDateFilter!: boolean

  @Prop({ type: String, default: '' })
  readonly dateColumn!: string

  @Prop({ type: Boolean, default: false })
  readonly hasActions!: boolean

  @Prop({ type: String, default: 'id' })
  readonly rowKey!: string

  @Prop({ type: Number, default: 10 })
  readonly defaultItemsPerPage!: number

  @Prop({ type: Array, default: () => [] })
  readonly dropdownFilters!: DropdownFilter[]

  @Prop({ type: Array, default: () => [] })
  readonly tabs!: (string | { key: string; label: string })[]

  @Prop({ type: String, default: '' })
  readonly alertMessage!: string

  @Prop({ type: Boolean, default: true })
  readonly showSettings!: boolean

  @Prop({ type: Boolean, default: false })
  readonly showCheckboxes!: boolean

  // Reactive data
  searchQuery = ''
  startDate = ''
  endDate = ''
  sortColumn = ''
  sortDirection: 'asc' | 'desc' = 'asc'
  currentPage = 1
  itemsPerPage = this.defaultItemsPerPage
  selectedFilters: Record<string, any> = {}
  localColumns: TableColumn[] = []
  draggedIndex: number | null = null
  selectedRows: any[] = []
  activeTab = ''
  visibleColumns: Record<string, boolean> = {}

  mounted() {
    this.itemsPerPage = this.defaultItemsPerPage
    this.localColumns = [...this.columns]

    this.localColumns.forEach(col => {
      this.visibleColumns[col.key] = true
    })

    if (this.tabs && this.tabs.length > 0) {
      this.activeTab = this.getTabKey(this.tabs[0])
    }
  }

  @Watch('columns', { deep: true })
  onColumnsPropChange(newVal: TableColumn[]) {
    this.localColumns = [...newVal]
  }

  get currentData(): any[] {
    return this.paginatedData
  }

  get totalItems(): number {
    return this.data.length
  }

  get hasActiveFilters(): boolean {
    const hasDropdownFilters = Object.values(this.selectedFilters).some(filter => {
      if (Array.isArray(filter)) return filter.length > 0
      return filter !== null && filter !== undefined && filter !== ''
    })
    return !!(this.searchQuery || this.startDate || this.endDate || hasDropdownFilters)
  }

  get isAllSelected(): boolean {
    return this.currentData.length > 0 && this.selectedRows.length === this.currentData.length
  }

  // Column Visibility Handlers
  isColumnVisible(key: string): boolean {
    return this.visibleColumns[key] !== false
  }

  resetColumnVisibility() {
    this.localColumns.forEach(col => {
      this.visibleColumns[col.key] = true
    })
  }

  // Drag and Drop Column Reordering Handlers
  onDragStart(event: DragEvent, index: number) {
    this.draggedIndex = index
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  onDrop(event: DragEvent, targetIndex: number) {
    if (this.draggedIndex !== null && this.draggedIndex !== targetIndex) {
      const movedItem = this.localColumns.splice(this.draggedIndex, 1)[0]
      this.localColumns.splice(targetIndex, 0, movedItem)
    }
    this.draggedIndex = null
  }

  // Tab Helpers
  getTabKey(tab: string | { key: string; label: string }): string {
    return typeof tab === 'string' ? tab : tab.key
  }

  getTabLabel(tab: string | { key: string; label: string }): string {
    return typeof tab === 'string' ? tab : tab.label
  }

  switchTab(tabKey: string) {
    this.activeTab = tabKey
    this.currentPage = 1
    this.$emit('tab-change', tabKey)
  }

  // Checkbox selection methods
  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedRows = []
    } else {
      this.selectedRows = [...this.currentData]
    }
  }

  isRowSelected(item: any): boolean {
    return this.selectedRows.some(row => row[this.rowKey] === item[this.rowKey])
  }

  toggleRowSelection(item: any) {
    const index = this.selectedRows.findIndex(row => row[this.rowKey] === item[this.rowKey])
    if (index > -1) {
      this.selectedRows.splice(index, 1)
    } else {
      this.selectedRows.push(item)
    }
  }

  setItemsPerPage(size: number) {
    this.itemsPerPage = size
    this.currentPage = 1
  }

  // Local client side filtering computed property
  get filteredData(): any[] {
    let filtered = [...this.data]

    // Search filter
    if (this.searchQuery && this.enableSearch) {
      const query = this.searchQuery.toLowerCase()
      filtered = filtered.filter(item => {
        return this.localColumns.some(column => {
          if (column.searchable === false) return false
          const value = this.getNestedValue(item, column.key)
          return value && value.toString().toLowerCase().includes(query)
        })
      })
    }

    // Date range filter
    if ((this.startDate || this.endDate) && this.enableDateFilter && this.dateColumn) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(this.getNestedValue(item, this.dateColumn))
        let matches = true

        if (this.startDate) {
          matches = matches && itemDate >= new Date(this.startDate)
        }
        if (this.endDate) {
          const endOfDay = new Date(this.endDate)
          endOfDay.setHours(23, 59, 59, 999)
          matches = matches && itemDate <= endOfDay
        }

        return matches
      })
    }

    // Tab Filter (filters status or state fields dynamically)
    if (this.activeTab && this.tabs.length > 0) {
      filtered = filtered.filter(item => {
        const val = (item.status || item.state || '').toString().toLowerCase()
        return val === this.activeTab.toLowerCase()
      })
    }

    // Dropdown filters
    for (const [filterKey, filterValue] of Object.entries(this.selectedFilters)) {
      if (!filterValue) continue

      filtered = filtered.filter(item => {
        const itemValue = this.getNestedValue(item, filterKey)
        
        if (Array.isArray(filterValue)) {
          return filterValue.includes(itemValue)
        } else {
          return itemValue === filterValue
        }
      })
    }

    // Sort data
    if (this.sortColumn) {
      filtered.sort((a, b) => {
        const aValue = this.getNestedValue(a, this.sortColumn)
        const bValue = this.getNestedValue(b, this.sortColumn)
        
        let comparison = 0
        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue
        } else if (aValue instanceof Date && bValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime()
        } else {
          comparison = aValue.toString().localeCompare(bValue.toString())
        }
        
        return this.sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return filtered
  }

  get totalPages(): number {
    return Math.ceil(this.filteredData.length / this.itemsPerPage) || 1
  }

  get startIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage
  }

  get endIndex(): number {
    return this.startIndex + this.itemsPerPage
  }

  get paginatedData(): any[] {
    return this.filteredData.slice(this.startIndex, this.endIndex)
  }

  get visiblePages(): number[] {
    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(this.totalPages, start + maxVisible - 1)

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    return pages
  }

  // Nested property plucker
  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  getRowKey(item: any, index: number): string | number {
    return this.getNestedValue(item, this.rowKey) || index
  }

  sortBy(column: string): void {
    const columnConfig = this.columns.find(col => col.key === column)
    if (columnConfig?.sortable === false) return

    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortColumn = column
      this.sortDirection = 'asc'
    }
    
    this.currentPage = 1
    this.$emit('sort', { column, direction: this.sortDirection })
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page
      this.$emit('page-change', page)
    }
  }

  clearAllFilters(): void {
    this.searchQuery = ''
    this.startDate = ''
    this.endDate = ''
    this.selectedFilters = {}
    this.currentPage = 1
    this.$emit('filter', { 
      search: '', 
      startDate: '', 
      endDate: '', 
      dropdownFilters: {} 
    })
  }

  formatDate(date: string | Date): string {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  formatFilterDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  formatCurrency(amount: number): string {
    return (amount || 0).toLocaleString()
  }

  getBadgeClass(value: string): string {
    const badgeMap: Record<string, string> = {
      'active': 'bg-success bg-opacity-10 text-success',
      'inactive': 'bg-danger bg-opacity-10 text-danger',
      'pending': 'bg-warning bg-opacity-10 text-warning',
      'completed': 'bg-primary bg-opacity-10 text-primary'
    }
    return badgeMap[value?.toLowerCase()] || 'bg-secondary bg-opacity-10 text-secondary'
  }

  getFilterDisplayText(filter: DropdownFilter): string {
    const selected = this.selectedFilters[filter.key]
    if (!selected) return filter.placeholder || filter.label
    
    if (Array.isArray(selected)) {
      return selected.length > 0 ? `${filter.label} (${selected.length})` : filter.label
    }
    
    const option = filter.options.find(opt => opt.value === selected)
    return option ? option.label : filter.label
  }

  getFilterLabel(key: string): string {
    const filter = this.dropdownFilters.find(f => f.key === key)
    return filter?.label || key
  }

  getFilterOptionLabel(key: string, value: any): string {
    const filter = this.dropdownFilters.find(f => f.key === key)
    const option = filter?.options.find(opt => opt.value === value)
    return option?.label || value
  }

  isFilterSelected(filterKey: string, value: any): boolean {
    const selected = this.selectedFilters[filterKey]
    if (Array.isArray(selected)) {
      return selected.includes(value)
    }
    return selected === value
  }

  toggleMultipleFilter(filterKey: string, value: any): void {
    if (!this.selectedFilters[filterKey]) {
      this.selectedFilters[filterKey] = []
    }
    
    const selected = this.selectedFilters[filterKey] as any[]
    const index = selected.indexOf(value)
    
    if (index > -1) {
      selected.splice(index, 1)
    } else {
      selected.push(value)
    }
    
    if (selected.length === 0) {
      delete this.selectedFilters[filterKey]
    }
    
    this.currentPage = 1
    this.$emit('filter', { 
      search: this.searchQuery, 
      startDate: this.startDate, 
      endDate: this.endDate,
      dropdownFilters: this.selectedFilters 
    })
  }

  setSingleFilter(filterKey: string, value: any): void {
    this.selectedFilters[filterKey] = value
    this.currentPage = 1
    this.$emit('filter', { 
      search: this.searchQuery, 
      startDate: this.startDate, 
      endDate: this.endDate,
      dropdownFilters: this.selectedFilters 
    })
  }

  clearFilter(filterKey: string): void {
    delete this.selectedFilters[filterKey]
    this.currentPage = 1
    this.$emit('filter', { 
      search: this.searchQuery, 
      startDate: this.startDate, 
      endDate: this.endDate,
      dropdownFilters: this.selectedFilters 
    })
  }
}

export default toNative(TableComponent)
</script>

<style scoped>
/* Main Card Style */
.custom-table-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Full Width Emulated Tab Underline Design */
.tabs-container {
  border-bottom: 1px solid #e5e7eb;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
}
.active-tab-style {
  color: #111827 !important;
}
.active-tab-line {
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #111827; /* Dark black border matching active tab in screenshot */
  border-radius: 99px;
}
.nav-link {
  cursor: pointer;
  font-size: 0.9rem;
  transition: color 0.15s ease-in-out;
}
.nav-link:hover:not(.active-tab-style) {
  color: #4b5563 !important;
}

/* Control Elements: Heights and Alignments aligned exactly like image reference */
.search-input-group {
  border-color: #d1d5db !important;
  border-radius: 6px;
  background-color: #ffffff;
  width: 220px;
  height: 38px;
  display: inline-flex;
  align-items: center;
}
.search-input-group .input-group-text {
  border: 0;
  background-color: transparent;
  padding-right: 0;
}
.search-input-group .form-control {
  font-size: 0.875rem;
  height: 100%;
  border: 0;
  box-shadow: none;
  background-color: transparent;
}
.date-range-picker {
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-width: 140px;
  height: 38px;
  padding: 0.375rem 0.75rem;
  background-color: #ffffff;
}
.filter-btn {
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  color: #4b5563;
  background-color: #ffffff;
  border-radius: 6px;
  height: 38px;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease-in-out;
}
.filter-btn:hover, .filter-icon-btn:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
  color: #111827;
}

.filter-icon-btn {
  width: 38px;
  height: 38px;
  font-size: 0.9rem;
  border: 1px solid #d1d5db;
  color: #4b5563;
  background-color: #ffffff;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;
}

/* Customize Columns Menu */
.customize-columns-dropdown-menu {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Table Style */
.custom-styled-table {
  width: 100%;
  border-collapse: collapse;
}
.bg-light-header {
  background-color: #f1f5f9 !important; /* Distinct light gray header */
}
.custom-styled-table th {
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  font-size: 0.8rem;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}
.custom-styled-table th,
.custom-styled-table td {
  border: 1px solid #e5e7eb !important; /* Subtle grid borders around every cell */
}
.table-row-hover:hover {
  background-color: #f1f5f9 !important;
  transition: background-color 0.1s ease-in-out;
}

/* Pagination arrows matching contacts image */
.pagination-arrow-btn {
  color: #9ca3af;
  padding: 0.25rem 0.5rem;
  transition: color 0.15s ease-in-out;
}
.pagination-arrow-btn:hover:not(:disabled) {
  color: #111827;
}
.pagination-arrow-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.items-per-page-toggle {
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 0;
  transition: color 0.15s ease-in-out;
}
.items-per-page-toggle:hover {
  color: #111827;
}
.text-underline {
  text-decoration: underline !important;
}

/* Alert Banner */
.alert-banner {
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  color: #c2410c;
}
.alert-icon-container {
  color: #ea580c;
  font-size: 1.15rem;
}

/* Visibility Cog Settings */
.hover-bg-light:hover {
  background-color: #f3f4f6;
}
.cursor-grab {
  cursor: grab;
}
.dropdown-item-custom:hover {
  background-color: #f9fafb;
}
</style>